import { POI } from '@cya-routes/shared'
import { getDistanceBetweenPoints } from './distanceUtils'


/**
 * Deduplicate POIs that are within a certain distance of each other
 * @param pois Array of POIs to deduplicate
 * @param radiusMeters Radius in meters to consider POIs as duplicates (default 500m)
 * @returns Deduplicated array of POIs
 */
export function deduplicatePOIs(pois: POI[], radiusMeters: number = 500): POI[] {
  if (!pois || pois.length === 0) return []
  
  const deduplicatedPOIs: POI[] = []
  const processedIndices = new Set<number>()
  
  pois.forEach((poi, index) => {
    // Skip if already processed as a duplicate
    if (processedIndices.has(index)) return
    
    // Skip POIs without coordinates
    if (!poi.coordinates?.lat || !poi.coordinates?.lng) {
      deduplicatedPOIs.push(poi)
      return
    }
    
    // Find all POIs within the radius that have the same name/type
    const duplicates: { poi: POI, index: number }[] = []
    
    pois.forEach((otherPoi, otherIndex) => {
      // Skip self and already processed
      if (otherIndex <= index || processedIndices.has(otherIndex)) return
      
      // Skip if no coordinates
      if (!otherPoi.coordinates?.lat || !otherPoi.coordinates?.lng) return
      
      // Check if same name/type and within radius
      if (poi.name === otherPoi.name && poi.type === otherPoi.type) {
        const distance = getDistanceBetweenPoints(
          poi.coordinates.lat,
          poi.coordinates.lng,
          otherPoi.coordinates.lat,
          otherPoi.coordinates.lng
        )
        
        if (distance <= radiusMeters) {
          duplicates.push({ poi: otherPoi, index: otherIndex })
        }
      }
    })
    
    // Mark all duplicates as processed
    duplicates.forEach(dup => processedIndices.add(dup.index))
    
    // Keep the first POI (or the one with more complete data)
    let bestPoi = poi
    
    // If there are duplicates, choose the one with the most complete data
    if (duplicates.length > 0) {
      const allPois = [{ poi, index }, ...duplicates]
      
      // Score POIs based on completeness
      const scoredPois = allPois.map(item => {
        let score = 0
        if (item.poi.description) score += 2
        if (item.poi.photos && item.poi.photos.length > 0) score += 3
        if (item.poi.tips) score += 1
        if (item.poi.hours) score += 1
        if (item.poi.website) score += 1
        return { ...item, score }
      })
      
      // Sort by score descending and take the best one
      scoredPois.sort((a, b) => b.score - a.score)
      bestPoi = scoredPois[0].poi
      
      console.log(`🗺️ Deduplicating POIs: "${poi.name}" - found ${duplicates.length} duplicates within ${radiusMeters}m, keeping best one with score ${scoredPois[0].score}`)
    }
    
    deduplicatedPOIs.push(bestPoi)
  })
  
  console.log(`🗺️ POI deduplication: ${pois.length} POIs → ${deduplicatedPOIs.length} unique POIs`)
  
  return deduplicatedPOIs
}

/**
 * Deduplicate POIs across multiple routes
 * @param routes Array of routes containing POIs
 * @param radiusMeters Radius in meters to consider POIs as duplicates (default 500m)
 * @returns Map of route ID to deduplicated POIs for that route
 */
export function deduplicatePOIsAcrossRoutes(
  routes: Array<{ id: string, pois?: POI[] }>,
  radiusMeters: number = 500
): Map<string, POI[]> {
  // Collect all POIs with their route IDs
  const allPOIsWithRoutes: Array<{ poi: POI, routeId: string }> = []
  
  routes.forEach(route => {
    if (route.pois) {
      route.pois.forEach(poi => {
        allPOIsWithRoutes.push({ poi, routeId: route.id })
      })
    }
  })
  
  // Create a global deduplicated list
  const processedIndices = new Set<number>()
  const routePOIsMap = new Map<string, POI[]>()
  
  // Initialize empty arrays for each route
  routes.forEach(route => {
    routePOIsMap.set(route.id, [])
  })
  
  allPOIsWithRoutes.forEach((item, index) => {
    if (processedIndices.has(index)) return
    
    const { poi, routeId } = item
    
    if (!poi.coordinates?.lat || !poi.coordinates?.lng) {
      // Keep POIs without coordinates
      const routePOIs = routePOIsMap.get(routeId) || []
      routePOIs.push(poi)
      routePOIsMap.set(routeId, routePOIs)
      return
    }
    
    // Find duplicates across all routes
    const duplicates: number[] = []
    
    allPOIsWithRoutes.forEach((otherItem, otherIndex) => {
      if (otherIndex <= index || processedIndices.has(otherIndex)) return
      
      const otherPoi = otherItem.poi
      if (!otherPoi.coordinates?.lat || !otherPoi.coordinates?.lng) return
      
      if (poi.name === otherPoi.name && poi.type === otherPoi.type) {
        const distance = getDistanceBetweenPoints(
          poi.coordinates.lat,
          poi.coordinates.lng,
          otherPoi.coordinates.lat,
          otherPoi.coordinates.lng
        )
        
        if (distance <= radiusMeters) {
          duplicates.push(otherIndex)
        }
      }
    })
    
    // Mark duplicates as processed
    duplicates.forEach(idx => processedIndices.add(idx))
    
    // Add POI only to its original route
    const routePOIs = routePOIsMap.get(routeId) || []
    routePOIs.push(poi)
    routePOIsMap.set(routeId, routePOIs)
  })
  
  return routePOIsMap
}